// Registry schemas + property-kind tables (ARCH §3.1, §3.3; BLK-1..3).
//
// Data describes; code acts (BLK-4). These zod schemas validate the JSON under
// data/**; the loader (load.ts) interns the validated data into the frozen
// Registries every system reads. zod is the ONE validation dependency (ARCH §1).
import { z } from 'zod';
import { ICON_SIZE } from './icon';

// ── Enumerated vocabularies (typos in data become validation errors) ─────────
export const MATERIALS = [
  'air', 'stone', 'dirt', 'grass', 'sand', 'gravel', 'wood', 'leaves',
  'plant', 'water', 'lava', 'glass', 'wool', 'metal', 'ice', 'snow',
] as const;
export const TOOL_CLASSES = ['pickaxe', 'axe', 'shovel', 'hoe', 'sword', 'shears'] as const;
export const TOOL_TIERS = ['wood', 'gold', 'stone', 'iron', 'diamond', 'netherite'] as const;
export const SHAPES = [
  'empty', 'cube', 'slab', 'stairs', 'cross', 'torch', 'fence', 'fence_gate',
  'wall', 'pane', 'door', 'trapdoor', 'button', 'plate', 'layer', 'farmland',
  'rail', 'fluid', 'chest', 'piston', 'wire', 'portal', 'bed', 'custom',
  /** Pointed dripstone: a tapered, collidable spike (shapes.ts SHAPE.DRIPSTONE). */
  'dripstone',
] as const;

// ── Block-state property kinds (BLK-3) ───────────────────────────────────────
// A block's `properties` map declares propName -> kind. The loader expands each
// kind to its canonical ORDERED value list. Enumeration is a mixed-radix counter
// with the FIRST-declared property most-significant and each property's values
// in the order below (booleans: false, true). This ordering IS the contract —
// the interning fixture test pins it.
export const FIXED_PROP_KINDS = {
  facing4: ['north', 'east', 'south', 'west'],
  facing6: ['north', 'east', 'south', 'west', 'up', 'down'],
  axis: ['x', 'y', 'z'],
  /** Nether-portal orientation — horizontal plane axis only (DIM-2). */
  portal_axis: ['x', 'z'],
  half: ['bottom', 'top'],
  hinge: ['left', 'right'],
  part_bed: ['foot', 'head'],
  part_door: ['lower', 'upper'],
  slab_type: ['bottom', 'top', 'double'],
  attachment: ['floor', 'wall', 'ceiling'],
  shape_stairs: ['straight', 'inner_left', 'inner_right', 'outer_left', 'outer_right'],
  shape_rail: [
    'north_south', 'east_west', 'ascending_east', 'ascending_west',
    'ascending_north', 'ascending_south', 'south_east', 'south_west',
    'north_west', 'north_east',
  ],
  oxidation: ['unaffected', 'exposed', 'weathered', 'oxidized'],
  /** Pointed dripstone growth direction + segment thickness (M19.1). */
  vertical_direction: ['up', 'down'],
  dripstone_thickness: ['tip', 'frustum', 'middle', 'base'],
  instrument: [
    'harp', 'basedrum', 'snare', 'hat', 'bass', 'flute', 'bell', 'guitar',
    'chime', 'xylophone', 'iron_xylophone', 'cow_bell', 'didgeridoo', 'bit',
    'banjo', 'pling',
  ],
  mode_comparator: ['compare', 'subtract'],
  // Boolean kinds — canonical order is false, true.
  open: ['false', 'true'],
  powered: ['false', 'true'],
  lit: ['false', 'true'],
  waterlogged: ['false', 'true'],
  triggered: ['false', 'true'],
  occupied: ['false', 'true'],
  persistent: ['false', 'true'],
  hanging: ['false', 'true'],
  waxed: ['false', 'true'],
  bottom: ['false', 'true'],
  extended: ['false', 'true'],
  short: ['false', 'true'],
  signal_fire: ['false', 'true'],
  /** End-portal frame: does this frame block hold an eye of ender? (M-end) */
  eye: ['false', 'true'],
  // Per-side connection booleans (fence/wall/pane/wire — one prop per side).
  north: ['false', 'true'],
  east: ['false', 'true'],
  south: ['false', 'true'],
  west: ['false', 'true'],
  up: ['false', 'true'],
  down: ['false', 'true'],
} as const satisfies Record<string, readonly string[]>;

export type FixedPropKind = keyof typeof FIXED_PROP_KINDS;

// Parameterized integer-range kinds — JSON supplies the bounds, e.g.
// `{ "age": { "kind": "age", "max": 7 } }` → values '0'..'7'.
export const PARAM_PROP_KINDS = [
  'age', 'level', 'distance', 'power', 'charges', 'honey_level', 'delay',
  'candles', 'eggs', 'pickles', 'rotation', 'stage', 'berries', 'layers', 'bites',
] as const;

const paramPropSchema = z.strictObject({
  kind: z.enum(PARAM_PROP_KINDS),
  min: z.number().int().optional(),
  max: z.number().int(),
});
export type ParamProp = z.infer<typeof paramPropSchema>;

/** A property value in block JSON: a fixed-kind name, or a parameterized range. */
export const propRefSchema = z.union([z.string(), paramPropSchema]);
export type PropRef = z.infer<typeof propRefSchema>;

const namePattern = /^[a-z][a-z0-9_]*$/;

/** A script name (ARCH §27.6). This is the SCRIPT LANGUAGE's own identifier rule,
 *  not `namePattern`: the lexer's `isIdentStart`/`isIdentPart` pair (script/lang/
 *  lex.ts) transcribed as one regex, because neither helper is exported and the
 *  language must not end up with a second, subtly different notion of a name.
 *  tests/script_mobdef.test.ts pins the equivalence against the real `tokenize`,
 *  so the two drift apart only with a red test. Reserved words are ident-SHAPED
 *  and stay legal here — a script's name is a file/overlay key, never parsed as
 *  source, so `repeat.tfs` is a fine name for a script about repeating. */
const scriptNamePattern = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** A painted 16×16 skin: palette + one base-36 index character per pixel
 *  (registry/icon.ts). Shared by item icons, block faces and mob rig parts. */
const iconSchema = z.strictObject({
  palette: z.array(z.string()).min(1).max(36),
  pixels: z.string().length(ICON_SIZE * ICON_SIZE),
});

// ── Blocks (BLK-1) ───────────────────────────────────────────────────────────
export const blockSchema = z
  .strictObject({
    name: z.string().regex(namePattern),
    properties: z.record(z.string(), propRefSchema).optional(),
    material: z.enum(MATERIALS),
    /** Break hardness; < 0 = unbreakable (bedrock/air). */
    hardness: z.number(),
    toolClass: z.enum(TOOL_CLASSES).optional(),
    /** Minimum tool tier required to yield drops (PLR-2). */
    toolTier: z.enum(TOOL_TIERS).optional(),
    lightEmission: z.number().int().min(0).max(15).default(0),
    lightOpacity: z.number().int().min(0).max(15).default(15),
    /** Mini-map top-surface colour (UI-8); falls back to the material colour. */
    mapColor: z.string().regex(/^#[0-9a-fA-F]{6}$/).optional(),
    shape: z.enum(SHAPES).default('cube'),
    dropSelf: z.boolean().optional(),
    lootTable: z.string().optional(),
    /** Attaches a block entity (per-position state) on place / structure gen (M6). */
    blockEntity: z.enum(['chest', 'furnace', 'smoker', 'campfire', 'spawner', 'hopper', 'dropper', 'dispenser', 'crafter', 'brewing_stand', 'script', 'world_door', 'decorated_pot', 'sticky_note', 'task_board', 'timer']).optional(),
    sounds: z.string().optional(),
    tags: z.array(z.string()).default([]),
    /** Painted skins for this block's atlas tiles (content editor). `all` covers
     *  every face; a named face overrides it. Absent ⇒ the procedural tiles.
     *  Face names map to the cube-face order in mesh/shapes/cube.ts. */
    texture: z.strictObject({
      all: iconSchema.optional(),
      west: iconSchema.optional(),
      east: iconSchema.optional(),
      bottom: iconSchema.optional(),
      top: iconSchema.optional(),
      north: iconSchema.optional(),
      south: iconSchema.optional(),
    }).optional(),
  })
  .refine((b) => !(b.dropSelf && b.lootTable), {
    message: 'block declares both dropSelf and lootTable — pick one (BLK-1)',
  });
export type BlockDef = z.infer<typeof blockSchema>;

// ── Items (BLK-6 / §3.4) ─────────────────────────────────────────────────────
export const itemSchema = z.strictObject({
  name: z.string().regex(namePattern),
  maxStack: z.number().int().min(1).max(64).default(64),
  /** For block items: the block this places. */
  block: z.string().optional(),
  /** Tool behaviour (PLR-2 break speed / drop gating; M4.3). */
  toolClass: z.enum(TOOL_CLASSES).optional(),
  toolTier: z.enum(TOOL_TIERS).optional(),
  /** Uses before the tool breaks (PLR-2 durability). */
  durability: z.number().int().positive().optional(),
  /** Base melee damage added on hit (PLR-3; combat lands M4.3/M17). */
  attackDamage: z.number().optional(),
  /** Furnace fuel burn time in game ticks (M6.2); absent ⇒ not a fuel. */
  fuelTicks: z.number().int().positive().optional(),
  /** Edible food (PLR-4; M11.2). `nutrition` = hunger points restored (half-
   *  drumsticks); `saturation` = Minecraft's saturation modifier. */
  food: z.strictObject({
    nutrition: z.number().int().min(0),
    saturation: z.number().min(0),
    /** Eatable even at full hunger (golden apple, honey). */
    alwaysEdible: z.boolean().default(false),
    /** Status effects granted on eating (golden apple, suspicious stew — MB.3).
     *  `seconds` 0 ⇒ instant effect; `chance` gates probabilistic grants. */
    effects: z.array(z.strictObject({
      effect: z.string(),
      amplifier: z.number().int().min(0).default(0),
      seconds: z.number().min(0).default(0),
      chance: z.number().min(0).max(1).default(1),
    })).optional(),
  }).optional(),
  tags: z.array(z.string()).default([]),
  /** Data-driven icon override (content editor). Absent ⇒ the item keeps its
   *  procedural sprite. See registry/icon.ts for the palette+pixels format. */
  icon: iconSchema.optional(),
});
export type ItemDef = z.infer<typeof itemSchema>;

// ── Recipes (§3.3; crafting/smelting). shaped/shapeless execute at M4.2; the
//    cooking/stonecutting/smithing families validate now, execute at M6+. ──────
export const RECIPE_TYPES = [
  'shaped', 'shapeless', 'smelting', 'blasting', 'smoking', 'campfire',
  'stonecutting', 'smithing',
] as const;
/** An ingredient is an item name or a `#tag` (matches any item carrying the tag). */
const ingredientSchema = z.string();
const recipeResultSchema = z.strictObject({
  item: z.string(),
  count: z.number().int().min(1).max(64).default(1),
});
export const recipeSchema = z.discriminatedUnion('type', [
  z.strictObject({
    type: z.literal('shaped'),
    name: z.string(),
    /** 1–3 rows, each 1–3 chars; ' ' is an empty cell. */
    pattern: z.array(z.string().min(1).max(3)).min(1).max(3),
    key: z.record(z.string().length(1), ingredientSchema),
    result: recipeResultSchema,
  }),
  z.strictObject({
    type: z.literal('shapeless'),
    name: z.string(),
    ingredients: z.array(ingredientSchema).min(1).max(9),
    result: recipeResultSchema,
  }),
  z.strictObject({
    type: z.enum(['smelting', 'blasting', 'smoking', 'campfire']),
    name: z.string(),
    ingredient: ingredientSchema,
    result: recipeResultSchema,
    /** Cook time in game ticks (default 200 = 10 s furnace). */
    time: z.number().int().positive().default(200),
    xp: z.number().min(0).default(0),
  }),
  z.strictObject({
    type: z.literal('stonecutting'),
    name: z.string(),
    ingredient: ingredientSchema,
    result: recipeResultSchema,
  }),
  z.strictObject({
    type: z.literal('smithing'),
    name: z.string(),
    base: ingredientSchema,
    addition: ingredientSchema,
    template: ingredientSchema.optional(),
    result: recipeResultSchema,
  }),
]);
export type RecipeDef = z.infer<typeof recipeSchema>;

// ── Loot tables (§3.3). Functions/conditions are validated but not executed
//    until M4/M6; the audit only needs the set of items a table can yield. ────
const lootEntrySchema = z.strictObject({
  type: z.enum(['item', 'empty', 'table']),
  name: z.string().optional(),
  weight: z.number().int().positive().default(1),
  functions: z.array(z.record(z.string(), z.unknown())).optional(),
  conditions: z.array(z.record(z.string(), z.unknown())).optional(),
});
const lootPoolSchema = z.strictObject({
  rolls: z.number().int().min(0).default(1),
  entries: z.array(lootEntrySchema).min(1),
});
export const lootSchema = z.strictObject({
  name: z.string(),
  pools: z.array(lootPoolSchema).min(1),
});
export type LootTable = z.infer<typeof lootSchema>;

// ── Enchantments (PLR-6; M12.1) ──────────────────────────────────────────────
export const enchantmentSchema = z.strictObject({
  id: z.string().regex(namePattern),
  /** Rarity weight (rarer = smaller). */
  weight: z.number().int().min(1),
  maxLevel: z.number().int().min(1),
  /** Item categories it applies to (sword/pickaxe/axe/shovel/…). */
  applies: z.array(z.string()).min(1),
  exclusiveWith: z.array(z.string()).default([]),
  /** Per-level cost window `[minCost, maxCost]`; index = level−1. */
  costs: z.array(z.tuple([z.number().int(), z.number().int()])).min(1),
});
export type EnchantmentDef = z.infer<typeof enchantmentSchema>;

// ── Sound groups (AUD stub — ids only until M23) ─────────────────────────────
export const soundGroupSchema = z.strictObject({
  name: z.string(),
  dig: z.string(),
  place: z.string(),
  step: z.string(),
  break: z.string().optional(),
  hit: z.string().optional(),
});
export type SoundGroup = z.infer<typeof soundGroupSchema>;

// ── Creative-only obtainability whitelist (BLK-5) ────────────────────────────
export const whitelistSchema = z.strictObject({
  items: z.array(z.string()).default([]),
});
export type Whitelist = z.infer<typeof whitelistSchema>;

// ── Biomes / terrain (GEN-3..5) ──────────────────────────────────────────────
const surfaceSchema = z.strictObject({
  top: z.string(),
  filler: z.string(),
  fillerDepth: z.number().int().min(1),
  under: z.string().optional(),
  underDepth: z.number().int().min(1).optional(),
});
export const biomeSchema = z.strictObject({
  name: z.string().regex(namePattern),
  surface: surfaceSchema,
  snowLine: z.number().int().optional(),
});
export type Biome = z.infer<typeof biomeSchema>;

const rangeSchema = z.tuple([z.number(), z.number()]);
export const placementRuleSchema = z.strictObject({
  biome: z.string(),
  temperature: rangeSchema.optional(),
  humidity: rangeSchema.optional(),
  continentalness: rangeSchema.optional(),
  erosion: rangeSchema.optional(),
  ridges: rangeSchema.optional(),
});
export type PlacementRule = z.infer<typeof placementRuleSchema>;

export const splineSchema = z.strictObject({
  continental: z.array(rangeSchema).min(2),
  erosion: z.array(rangeSchema).min(2),
});
export type TerrainSpline = z.infer<typeof splineSchema>;

// ── Ore/feature distributions (GEN-7) ────────────────────────────────────────
const distEntrySchema = z.union([
  z.strictObject({ kind: z.literal('triangular'), min: z.number().int(), peak: z.number().int(), max: z.number().int(), weight: z.number().positive().optional() }),
  z.strictObject({ kind: z.literal('uniform'), min: z.number().int(), max: z.number().int(), weight: z.number().positive().optional() }),
]);
export const oreConfigSchema = z.strictObject({
  block: z.string(),
  deepslate: z.string().optional(),
  veinSize: z.number().int().min(1),
  tries: z.number().int().min(0),
  dist: z.array(distEntrySchema).min(1),
  /** Surface biomes this ore may generate under (empty = anywhere). Depth alone
   *  hides most ores where they belong, but not all of them: emerald's band runs so
   *  high that only a mountain has stone up there anyway — while a badlands' extra
   *  gold has no depth signature at all and needs a real biome gate. Two entries for
   *  the same block with different biomes give one ore two distributions. */
  biomes: z.array(z.string()).default([]),
});
export type OreConfig = z.infer<typeof oreConfigSchema>;

// ── Trees + vegetation (GEN-8) ───────────────────────────────────────────────
// Data picks WHICH species appear in a biome and HOW dense; the tree geometry
// itself is code (src/sim/gen/overworld/trees.ts). `perChunk` is an expected
// count (fractional allowed → probabilistic) of placement attempts per chunk.
export const TREE_KINDS = ['oak', 'birch', 'spruce', 'cherry'] as const;
const treeTypeSchema = z.strictObject({
  kind: z.enum(TREE_KINDS),
  weight: z.number().positive().default(1),
});
const treeRuleSchema = z.strictObject({
  biomes: z.array(z.string()).min(1),
  perChunk: z.number().min(0),
  types: z.array(treeTypeSchema).min(1),
});
export type TreeRule = z.infer<typeof treeRuleSchema>;

const patchRuleSchema = z
  .strictObject({
    block: z.string(),
    biomes: z.array(z.string()).min(1),
    /** Surface block names this patch may sit on (e.g. grass_block, sand). */
    on: z.array(z.string()).min(1),
    perChunk: z.number().min(0),
    minHeight: z.number().int().min(1).default(1),
    maxHeight: z.number().int().min(1).default(1),
    /** Require a water column horizontally adjacent (sugar cane). */
    nextToWater: z.boolean().default(false),
    /** Require no taller solid neighbor (cactus can't be walled in). */
    clearNeighbors: z.boolean().default(false),
  })
  .refine((p) => p.maxHeight >= p.minHeight, { message: 'patch maxHeight < minHeight' });
export type PatchRule = z.infer<typeof patchRuleSchema>;

export const vegetationSchema = z.strictObject({
  trees: z.array(treeRuleSchema).default([]),
  patches: z.array(patchRuleSchema).default([]),
});
export type Vegetation = z.infer<typeof vegetationSchema>;

export const featuresSchema = z.strictObject({
  ores: z.array(oreConfigSchema).default([]),
  vegetation: vegetationSchema.optional(),
});
export type Features = z.infer<typeof featuresSchema>;

// ── Mobs (MOB-1..5; ARCH §3.3). Data describes the entity; behaviour is the
//    archetype code (src/sim/entities/archetypes/…) plus per-name overrides
//    (src/sim/entities/mobs/…). The client builds a box rig from `rig`. ────────
export const MOB_CATEGORIES = ['monster', 'creature', 'ambient', 'water_creature', 'water_ambient'] as const;
/** Movement medium (MOB-3 physics). `ground` walks and steers around water;
 *  `amphibious` walks but is willing to enter it (drowned, turtle, frog);
 *  `air` flies in 3D ignoring gravity; `water` swims confined to fluid. */
export const MOB_MOVEMENTS = ['ground', 'amphibious', 'air', 'water'] as const;
export const MOB_ARCHETYPES = ['grazer', 'melee', 'ranged', 'flying', 'tamable', 'golem', 'ambient', 'villager', 'fish'] as const;

/** One box of a mob's render rig (client-only geometry; sim ignores it). Sizes
 *  and offsets are in blocks, relative to the entity's feet-centre origin. */
const rigPartSchema = z.strictObject({
  name: z.string(),
  size: z.tuple([z.number(), z.number(), z.number()]),
  /** Centre offset of the box from the entity origin (feet centre). */
  pos: z.tuple([z.number(), z.number(), z.number()]),
  /** Hex colour string, e.g. "#6a8a3a". Used flat when `texture` is absent, and
   *  as the tint the texture is drawn against. */
  color: z.string(),
  /** Optional painted skin for this box (content editor). `all` covers every
   *  side of the box; a named side overrides it. Absent ⇒ flat colour. */
  texture: z.strictObject({
    all: iconSchema.optional(),
    west: iconSchema.optional(),
    east: iconSchema.optional(),
    bottom: iconSchema.optional(),
    top: iconSchema.optional(),
    north: iconSchema.optional(),
    south: iconSchema.optional(),
  }).optional(),
  /** Animate this part as a leg (swings while walking). */
  leg: z.boolean().default(false),
  /** Animate this part as the head (looks toward the target). */
  head: z.boolean().default(false),
});

const mobSpawnSchema = z.strictObject({
  category: z.enum(MOB_CATEGORIES),
  /** Which dimension this mob spawns in (MOB-4; nether mobs stay in the nether). */
  dimension: z.enum(['overworld', 'nether', 'any']).default('overworld'),
  /** Biome names this mob may spawn in (empty = any). */
  biomes: z.array(z.string()).default([]),
  /** Relative spawn weight within its category. */
  weight: z.number().int().min(0).default(10),
  packMin: z.number().int().min(1).default(1),
  packMax: z.number().int().min(1).default(1),
  /** Requires a grass-like surface + sky access (passive creatures). */
  surface: z.boolean().default(false),
  /** Blocks this mob may stand on, replacing BOTH its category's default ground
   *  test and its light minimum. The nether has no grass and no daylight, so a
   *  hoglin (crimson nylium) and a strider (the lava itself) can't be described
   *  any other way. Empty = the category default. */
  ground: z.array(z.string()).default([]),
  /** Placed SUBMERGED rather than in an air pocket, while keeping its own
   *  category — a guardian is a monster that lives in water, and without this it
   *  could never spawn at all: an ocean column's surface is the air above the
   *  waves, which has no solid floor under it. */
  aquatic: z.boolean().default(false),
  /** Only spawns inside this structure's footprint (the elder guardian). */
  structure: z.string().optional(),
});

// ── Heredity gene tables (HER-2, ARCH §28.3) ─────────────────────────────────
// A species' GENE TABLE: its genes in genome-layout order (§28.3 — "gene order in the
// table IS the genome layout", so reordering a shipped table is save-breaking). The
// structural rules live here; the cross-field rules (dominant ∈ alleles, freq sums to
// 1, parts exist in the rig, the scalar is known) are `heredity/genes.ts`'
// `validateGenes`, run at registry load.
const hexColour = z.string().regex(/^#[0-9a-fA-F]{6}$/);
const mendelianGeneSchema = z.strictObject({
  name: z.string().regex(namePattern),
  kind: z.literal('mendelian'),
  /** 2..4 allele names; index order is what a genome byte stores. */
  alleles: z.array(z.string().min(1)).min(2).max(4),
  dominant: z.string(),
  /** Wild-type allele frequencies, one per allele, summing to 1. */
  freq: z.array(z.number().min(0).max(1)),
  expresses: z.strictObject({
    /** Paint these rig parts by the expressed allele. */
    tint: z.object({ parts: z.array(z.string()).min(1) }).catchall(z.union([hexColour, z.array(z.string())])).optional(),
    /** Show / hide one rig part by the expressed allele. */
    part: z.object({ name: z.string() }).catchall(z.union([z.boolean(), z.string()])).optional(),
    /** A natural `variant` by the expressed allele — a dye colour name (the sheep's wool,
     *  §28.3/§28.6). */
    variant: z.record(z.string(), z.string()).optional(),
  }),
});
const polygenicGeneSchema = z.strictObject({
  name: z.string().regex(namePattern),
  kind: z.literal('polygenic'),
  expresses: z.strictObject({
    /** The `Traits` scalar this gene sets (§28.6). */
    scalar: z.string(),
    /** Byte 0 → `range[0]`, byte 255 → `range[1]`; an inverted pair is deliberate. */
    range: z.tuple([z.number(), z.number()]),
  }),
});
export const geneSchema = z.discriminatedUnion('kind', [mendelianGeneSchema, polygenicGeneSchema]);
export type Gene = z.infer<typeof geneSchema>;
export type MendelianGene = z.infer<typeof mendelianGeneSchema>;
export type PolygenicGene = z.infer<typeof polygenicGeneSchema>;

export const mobSchema = z.strictObject({
  name: z.string().regex(namePattern),
  archetype: z.enum(MOB_ARCHETYPES),
  category: z.enum(MOB_CATEGORIES),
  /** How this mob moves, independent of its AI archetype. Omitted = inferred:
   *  water for water categories, air for the flying/ambient archetypes, else
   *  ground. The override exists because `ambient` is an AI style, not a medium —
   *  a turtle and a bat share it but only one of them should leave the floor. */
  movement: z.enum(MOB_MOVEMENTS).optional(),
  width: z.number().positive(),
  height: z.number().positive(),
  maxHealth: z.number().positive(),
  /** Ground movement speed in blocks/second. */
  speed: z.number().min(0),
  /** Melee attack damage on normal difficulty (melee/ranged mobs). */
  attackDamage: z.number().min(0).optional(),
  /** Aggro/target-acquisition range in blocks (melee/ranged). */
  followRange: z.number().min(0).default(16),
  /** XP dropped on death by a player kill. */
  xp: z.number().int().min(0).default(0),
  /** Loot table name (data/loot/…); resolved on death (MOB-4). */
  drops: z.string().optional(),
  /** Storage slots this mob carries once a chest is strapped to it (donkey/mule/
   *  llama). 0 = it carries nothing, which is every other mob. */
  chestSlots: z.number().int().min(0).default(0),
  /** Grazer breeding food item (e.g. wheat); enables MOB-2 breeding. */
  breedItem: z.string().optional(),
  /** Tamable food item (bone → wolf); enables MOB-2 taming (tamable archetype). */
  tameItem: z.string().optional(),
  /** Per-feed taming success chance (0..1); the roll is seeded (MOB-2). */
  tameChance: z.number().min(0).max(1).default(1 / 3),
  /** Neutral (not hostile until provoked) — a struck one turns angry (MOB-2 wolf). */
  neutral: z.boolean().default(false),
  /** Ranged projectile type + cadence. */
  projectile: z.string().optional(),
  fireInterval: z.number().int().positive().default(40),
  standoffMin: z.number().min(0).default(4),
  standoffMax: z.number().min(0).default(15),
  /** Burns in daylight (zombie/skeleton) — the M5.2 override reads this. */
  burnsInSunlight: z.boolean().default(false),
  /** Slime-like: size lives in `variant` (1/2/4); AABB, health, and damage scale
   *  with it, and it splits into smaller copies on death (M13.2). */
  sized: z.boolean().default(false),
  /** Number of cosmetic liveries this mob rolls between on spawn (tropical fish).
   *  Absent/1 ⇒ variant 0, exactly as every mob behaved before. */
  variants: z.number().int().min(1).optional(),
  /** Villager profession trade table (data/trades/…) — enables trading (VIL, M14.1). */
  trades: z.string().optional(),
  /** Illager raider (pillager/vindicator/ravager…) — fills raid waves; a captain
   *  (variant 1) grants Bad Omen when a player kills it (RAID, M14.2). */
  raider: z.boolean().default(false),
  /** Bartering piglin — trades a gold ingot for a nether trinket (M20). */
  barters: z.boolean().default(false),
  /** Status effect inflicted on a hit target (husk → hunger, stray → slowness). */
  onHitEffect: z.strictObject({
    effect: z.string(),
    amplifier: z.number().int().min(0).default(0),
    seconds: z.number().positive(),
    chance: z.number().min(0).max(1).default(1),
  }).optional(),
  /** Behaviour script name (SCR-7; ARCH §27.6) — resolves to `data/scripts/<name>.tfs`
   *  or an overlay-provided source. Its hooks run BEFORE `behaviorFor(type,
   *  archetype)` and fall through to it unless the script calls `handled()`.
   *  **Optional with no default on purpose:** absent ⇒ the key is absent from the
   *  parsed entry, so every unscripted mob interns byte-identically to pre-M45 —
   *  a default (even `''`) would add a field to all 75 shipped mobs. Being an
   *  ordinary registry field, the sparse overlay, the archive export and
   *  collection versioning already carry it with no new pipeline. */
  script: z.string().regex(scriptNamePattern).optional(),
  /** Script dispatch rate in Hz (ARCH §27.8) — `5` (the default cadence, one dispatch
   *  every `SCR.MOB.TICK_DIVISOR` ticks) or `20` (every tick). **Opting in to 20 Hz
   *  quarters the per-dispatch op budget**, so a mob's ops/second are the same either
   *  way; the coupling lives in `script/system.ts`, which is the one place that turns
   *  this field into a divisor and a budget.
   *  **Optional with no default, exactly like `script` above:** absent ⇒ the key is
   *  absent from the parsed entry, so every shipped mob interns byte-identically and
   *  `pnpm audit:registry` does not move. A default (even `5`) would add a field to
   *  all 75 of them. */
  scriptHz: z.union([z.literal(5), z.literal(20)]).optional(),
  /** A flying mob HUNTS this species by name (§29.11, CNT-3 barn_owl): below
   *  `OWL.HUNT_LIGHT_MAX` block light it targets the nearest match within
   *  `OWL.HUNT_RADIUS` instead of the flying archetype's normal player-attack
   *  behaviour. **Optional with no default, exactly like `script`:** absent ⇒
   *  absent, so every existing flyer (ghast, phantom, bee) interns byte-identically
   *  and keeps its player-attack path untouched. */
  hunts: z.string().regex(namePattern).optional(),
  /** Heredity gene table (HER-2, ARCH §28.3), in genome-layout order. **Optional with no
   *  default**, like `script`: absent ⇒ absent, so a species without genes interns
   *  byte-identically and every rule-off path is untouched. */
  genes: z.array(geneSchema).optional(),
  spawn: mobSpawnSchema,
  /** Client render rig (MOB-1: description lives in the registry). */
  rig: z.array(rigPartSchema).default([]),
});
export type MobDef = z.infer<typeof mobSchema>;
export type MobSpawn = z.infer<typeof mobSpawnSchema>;

// ── Villager trades (VIL; ARCH §3 data/trades — M14.1) ───────────────────────
const tradeStackSchema = z.strictObject({
  item: z.string(),
  count: z.number().int().positive().default(1),
});
export const tradeOfferSchema = z.strictObject({
  /** 1–2 input stacks the villager wants. */
  buy: z.array(tradeStackSchema).min(1).max(2),
  /** The item stack handed back. */
  sell: tradeStackSchema,
  /** Uses before the offer locks until the villager restocks (VIL). */
  maxUses: z.number().int().positive().default(12),
  /** Villager XP granted on each use (profession level-up). */
  xp: z.number().int().min(0).default(2),
});
export const tradeTableSchema = z.strictObject({
  name: z.string(),
  offers: z.array(tradeOfferSchema).min(1),
});
export type TradeOffer = z.infer<typeof tradeOfferSchema>;
export type TradeTable = z.infer<typeof tradeTableSchema>;

// ── Raid wave tables (RAID; ARCH §3 data/raids — M14.2) ──────────────────────
// A wave is a mob-name → count map; code (game/raid.ts) expands it into the
// raiders it spawns. Waves scale by difficulty; peaceful never raids (BLK-4).
const raidWaveSchema = z
  .record(z.string().regex(namePattern), z.number().int().positive())
  .refine((w) => Object.keys(w).length > 0, { message: 'raid wave has no raiders' });
export const raidTableSchema = z.strictObject({
  name: z.string(),
  waves: z.strictObject({
    easy: z.array(raidWaveSchema).min(1),
    normal: z.array(raidWaveSchema).min(1),
    hard: z.array(raidWaveSchema).min(1),
  }),
});
export type RaidWave = z.infer<typeof raidWaveSchema>;
export type RaidTable = z.infer<typeof raidTableSchema>;

// ── Lesson packs (EDU-1, ARCH §29.2) ─────────────────────────────────────────
// A lesson pack is an opt-in collection version: the `lessons` overlay kind,
// entries keyed by `name` like every other registry (D-TF-188). Structural shape
// only, here; the cross-field rules (unique objective names, acyclic `after`,
// declared regions, referenced content, the template, known gamerules) are
// `education/lessons.ts`'s `validateLesson`, run at registry load — same split as
// heredity's gene tables (`heredity/genes.ts` `validateGenes`).
//
// `name` allows one `/` and hyphens — "biology/bolder-flock" — unlike every other
// registry's bare identifier, because a lesson id doubles as its subject grouping
// (§29.3's create-screen tab groups by the segment before the slash) and every
// §29.12 v1 lesson name is hyphenated ("fly-the-pattern", "tune-the-autopilot").
const lessonNamePattern = /^[a-z][a-z0-9-]*\/[a-z][a-z0-9-]*$/;

/** The ten lesson-only trigger kinds (`sim/game/advancements.ts` `Trigger` union,
 *  §29.4). A criterion's `min`/`max` are generic per-field bounds rather than one
 *  fixed numeric field, because `autopilot_hold` needs BOTH `error` and `ticks`
 *  bounded from the same firing (§29.12 "tune-the-autopilot": error ≤ 5 for
 *  ticks ≥ 600) — a bound keyed by field name covers every kind uniformly, and
 *  which keys are actually meaningful for a kind is `compileCriterion`'s job
 *  (M51.2), not this schema's.
 *
 *  `population_count`/`capacity_message` are M53.5's addition (CNT-5, the
 *  `biology/population` lesson: "reach 20 voles" / "release 2 owls" / "voles
 *  below 10" / "read the capacity message"). Neither fits the original eight —
 *  a THRESHOLD on a live world count, and a bare event, are both new shapes —
 *  so a lesson pack can name them the same way it names any other trigger,
 *  rather than the population lesson going unbuilt. `population_count` covers
 *  BOTH "reach 20" (`min: {value: 20}`) and "below 10" (`max: {value: 9}`) with
 *  one kind — a live count crossing either bound is the same observable fact. */
const criterionCommon = {
  min: z.record(z.string(), z.number()).optional(),
  max: z.record(z.string(), z.number()).optional(),
  count: z.number().int().min(1).optional(),
};
export const criterionSchema = z.discriminatedUnion('trigger', [
  z.strictObject({ trigger: z.literal('instrument_read'), instrument: z.string(), metric: z.string(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('notebook_record'), species: z.string(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('region_entered'), region: z.string(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('mob_bred'), species: z.string(), recordedParents: z.boolean().optional(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('crop_harvested'), crop: z.string(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('beacon_found'), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('aircraft_landed'), runway: z.boolean().optional(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('autopilot_hold'), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('population_count'), species: z.string(), ...criterionCommon }),
  z.strictObject({ trigger: z.literal('capacity_message'), ...criterionCommon }),
]);
export type CriterionData = z.infer<typeof criterionSchema>;
export type EduTriggerKind = CriterionData['trigger'];

export const objectiveSchema = z.strictObject({
  name: z.string().regex(namePattern),
  title: z.string(),
  hint: z.string().optional(),
  after: z.string().regex(namePattern).optional(),
  criteria: z.array(criterionSchema).min(1),
});
export type ObjectiveDef = z.infer<typeof objectiveSchema>;

export const questionSchema = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('choice'), prompt: z.string(), options: z.array(z.string()).min(2), answer: z.number().int().min(0) }),
  z.strictObject({ kind: z.literal('numeric'), prompt: z.string(), answer: z.number(), tolerance: z.number().min(0), unit: z.string().optional() }),
  z.strictObject({ kind: z.literal('punnett'), prompt: z.string(), a: z.string(), b: z.string() }),
]);
export type QuestionData = z.infer<typeof questionSchema>;

export const quizSchema = z.strictObject({
  after: z.string().regex(namePattern).optional(),
  questions: z.array(questionSchema).min(1),
});
export type QuizDef = z.infer<typeof quizSchema>;

const regionSchema = z.strictObject({
  name: z.string().regex(namePattern),
  min: z.tuple([z.number(), z.number(), z.number()]),
  max: z.tuple([z.number(), z.number(), z.number()]),
});
export type RegionDef = z.infer<typeof regionSchema>;

export const lessonSchema = z.strictObject({
  name: z.string().regex(lessonNamePattern),
  title: z.string(),
  subject: z.string(),
  description: z.string(),
  gamerules: z.record(z.string(), z.string()).optional(),
  /** Inline `.tfworld` JSONL (D-TF-189), parsed + size-checked by `validateLesson`. */
  template: z.string().optional(),
  spawn: z.tuple([z.number(), z.number(), z.number()]).optional(),
  regions: z.array(regionSchema).optional(),
  /** ELT beacons to deploy when this lesson's world is entered (M54.2, CNT-5
   *  "ground-team/find-the-elt": "ELT placed by the template"). A beacon is
   *  session-scoped live state (no persistence exists for it at all, in either
   *  host — D-TF-277), so it is LESSON content re-deployed fresh on every entry,
   *  the same way `regions`/`spawn` are lesson-level rather than baked into the
   *  generic `.tfworld` format. */
  beacons: z.array(z.tuple([z.number(), z.number(), z.number()])).optional(),
  objectives: z.array(objectiveSchema).min(1),
  quiz: quizSchema.optional(),
});
export type LessonDef = z.infer<typeof lessonSchema>;

/** Resolve one block's `properties` map to ordered [name, values[]] pairs in
 *  declaration order. Throws on an unknown fixed kind (BLK-3 is a closed set). */
export function resolvePropertyValues(
  blockName: string,
  properties: Record<string, PropRef> | undefined,
): Array<readonly [string, readonly string[]]> {
  if (!properties) return [];
  return Object.entries(properties).map(([propName, ref]) => {
    if (typeof ref === 'string') {
      const values = (FIXED_PROP_KINDS as Record<string, readonly string[]>)[ref];
      if (!values) {
        throw new Error(
          `block '${blockName}' property '${propName}' names unknown kind '${ref}' (BLK-3)`,
        );
      }
      return [propName, values] as const;
    }
    const min = ref.min ?? 0;
    if (ref.max < min) {
      throw new Error(`block '${blockName}' property '${propName}' has max < min`);
    }
    const values: string[] = [];
    for (let v = min; v <= ref.max; v++) values.push(String(v));
    return [propName, values] as const;
  });
}
